import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, ExternalLink, Linkedin } from 'lucide-react';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-100px" },
  transition: { staggerChildren: 0.15 }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
};

const blogs = [
  {
    id: 1,
    slug: 'ai-and-future-of-marketing',
    title: 'AI IS NOT REPLACEMENT. IT IS AMPLIFICATION.',
    excerpt: 'Strip away the headline drama from Vinod Khosla\'s statement, and the core message is difficult to dispute. AI isn\'t nibbling at the edges anymore — it\'s moving straight into territory once considered automation-proof.',
    date: 'December 2024',
    linkedinUrl: 'https://www.linkedin.com/posts/shashank-joshi-a05b4732_a-couple-of-days-ago-mr-vinod-khosla-made-activity-7430815990327730176-N14-',
    image: '/blog-ai-3d.png'
  },
  {
    id: 2,
    slug: 'social-media-metrics-2026',
    title: 'THE "LIKE" BUTTON IS OFFICIALLY A VANITY METRIC',
    excerpt: 'If you\'re still reporting reach and likes in 2026, you\'re measuring the past. The algorithm hasn\'t broken — it has matured. We\'ve moved from the Feed Era to the Chat Era.',
    date: 'November 2024',
    linkedinUrl: 'https://www.linkedin.com/posts/shashank-joshi-a05b4732_the-like-button-is-officially-a-vanity-activity-7413797975468044288-7wYw',
    image: '/blog-social-cover.jpg'
  }
];

const BlogsPage = () => {
  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div {...fadeInUp} className="text-center">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">BLOG</span>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 uppercase">
              INSIGHTS & PERSPECTIVES
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto">
              Thoughts on marketing, technology, and the evolving digital landscape
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-gray-50" data-testid="blog-list">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...staggerContainer} className="space-y-12">
            {blogs.map((blog) => (
              <motion.article 
                key={blog.id} 
                variants={staggerItem}
                className="bg-white rounded-3xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-500"
              >
                {/* Blog Image */}
                <div className="h-64 md:h-80 overflow-hidden">
                  <img 
                    src={blog.image} 
                    alt={`${blog.title} - Blog by Shashank Joshi, Founder Zenith Marketing India`}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Content */}
                <div className="p-8 md:p-10">
                  {/* Author Info */}
                  <div className="flex items-center gap-4 mb-6">
                    <img 
                      src="/founder-shashank-new.jpg" 
                      alt="Shashank Joshi"
                      className="w-14 h-14 rounded-full object-cover border-2 border-brand-blue"
                    />
                    <div>
                      <a 
                        href="https://in.linkedin.com/in/shashank-joshi-a05b4732"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-gray-900 hover:text-brand-blue transition-colors flex items-center gap-2 uppercase"
                      >
                        SHASHANK JOSHI
                        <Linkedin size={16} className="text-blue-600" />
                      </a>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {blog.date}
                      </p>
                    </div>
                  </div>
                  
                  <h2 className="font-outfit text-2xl md:text-3xl font-bold text-gray-900 mb-4 uppercase">
                    {blog.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                    {blog.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to={`/blog/${blog.slug}`}
                      className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-medium hover:from-cyan-400 hover:to-brand-blue hover:shadow-lg transition-all duration-300 uppercase"
                    >
                      READ FULL ARTICLE
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                    <a
                      href={blog.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 rounded-full border-2 border-blue-600 text-blue-600 font-medium hover:bg-blue-600 hover:text-white transition-all duration-300 uppercase"
                    >
                      <Linkedin size={18} className="mr-2" />
                      VIEW ON LINKEDIN
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-brand-blue via-cyan-500 to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
              WANT TO DISCUSS MARKETING STRATEGY?
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Let's explore how these insights can transform your brand
            </p>
            <Link
              to="/contact"
              data-testid="blog-contact-btn"
              className="inline-flex items-center px-10 py-4 rounded-full bg-white text-brand-blue font-medium text-lg transition-all duration-300 hover:bg-gray-100 hover:scale-105 uppercase"
            >
              GET IN TOUCH
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;
